// "Assign to me" from the Unassigned list: take the ticket, then open it under Assigned to me.
// If someone (or the router) got it first, or the Sentinel is already at their cap in the
// brand, say so and refresh the lists.

import { useCallback, useState } from 'react';
import { useSetAtom } from 'jotai';
import axios from 'axios';
import { toast } from '@/components/ui/sonner';
import { invalidateQueries } from '@/lib/query-client';
import { markOwnTicketAction } from '@/lib/sentinel-own-actions';
import { selectedSentinelSectionAtom } from '@/hooks/use-sentinel-routing';
import { sentinelWorkspaceService } from '@/services/sentinelWorkspace.service';

export interface AssignToMe {
  assign: (ticketId: number) => Promise<void>;
  /** The ticket being taken right now, so its button can show progress. */
  pendingTicketId: number | null;
}

/** The server's own words for a cap refusal, which carry the counts ("5 of 5"). */
function capMessage(error: unknown): string | null {
  if (!axios.isAxiosError(error)) return null;
  const message = (error.response?.data as { message?: unknown } | undefined)?.message;
  return typeof message === 'string' && message.trim() ? message : null;
}

function refreshLists(): void {
  void invalidateQueries(['sentinel-workspace', 'tickets']);
  void invalidateQueries(['sentinel', 'unassigned-counts']);
}

export function useAssignToMe(openTicket: (ticketId: number) => void): AssignToMe {
  const setSection = useSetAtom(selectedSentinelSectionAtom);
  const [pendingTicketId, setPendingTicketId] = useState<number | null>(null);

  const assign = useCallback(
    async (ticketId: number) => {
      setPendingTicketId(ticketId);
      // Before the request: its "assigned to you" event can beat the response.
      markOwnTicketAction(ticketId);
      try {
        await sentinelWorkspaceService.assignToMe(ticketId);
        toast.success('Ticket assigned to you');
        setSection('mine');
        openTicket(ticketId);
      } catch (error) {
        const status = axios.isAxiosError(error) ? error.response?.status : undefined;
        if (status === 409) {
          toast.error('Someone else just took this ticket');
        } else if (status === 422) {
          toast.error(capMessage(error) ?? 'You’re at your limit for this brand. Send or close a ticket first.');
        } else {
          toast.error('Could not assign the ticket to you. Please try again.');
        }
      } finally {
        refreshLists();
        setPendingTicketId(null);
      }
    },
    [openTicket, setSection],
  );

  return { assign, pendingTicketId };
}
